import "./Validate.scss";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { useTypeSelector } from "../../../hooks/useTypeSelector";
import { useActions } from "../../../hooks/useActions";
import { PathologyParams, PathologyPutParams } from "../../../types/pathology";
import { Loader } from "../../../components";
import { NotificationSend } from "../../../utils/Notification/Notification";
import axios from "axios";
import { Markup } from "../../../components/Markup/Markup";
import { useRecoilState } from "recoil";
import {
  imageDataState,
  imageNameState,
  markupPathologyState,
  markupState,
  markupStepsState,
  pathologyImagesState,
  regionState,
} from "../../../components/Markup/markupAtoms";
import visibleParts from "./visibleParts";
import projections from "./projections";
import modes from "./modes";
import { ImageData, markupDataParams } from "../../../types/markup";
import regionConfig from "./regionConfig";

export const Validate = (): JSX.Element => {
  const { pathology, loading, error } = useTypeSelector(
    (state) => state.pathology
  );

  const [defaultDataState, setDefaultDataState] = useState<
    PathologyParams | PathologyPutParams | null
  >(null);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [image, setImage] = useState<string>("");
  const markupPathologyRef = useRef(
    {} as {
      pathology: PathologyParams;
      markup: Record<string, markupDataParams[]>;
    }
  );

  const [markupPathology, setMarkupPathology] =
    useRecoilState(markupPathologyState);

  const [pathologyImages, setPathologyImages] =
    useRecoilState(pathologyImagesState);

  const [imageName, setImageName] = useRecoilState(imageNameState);

  const [, setMarkupSteps] = useRecoilState(markupStepsState);
  const [, setImageData] = useRecoilState(imageDataState);
  const [markup] = useRecoilState(markupState);

  const {
    getMarkupPathology,
    getPathology,
    validatePathology,
    saveMarkUpPathology,
    deletePathology,
  } = useActions();

  const isMarkup = useMemo(() => location.pathname.includes("/markup"), []);

  const { search } = useLocation();
  const region = useMemo(
    () => new URLSearchParams(search).get("region") || "",
    [search]
  );

  useEffect(() => {
    markupPathologyRef.current.markup = {};
    setMarkupPathology({});
    isMarkup ? getMarkupPathology(region) : getPathology(region);

    return () => {
      setMarkupPathology({});
      setImage("");
      setImageName("");

      postMarkup();
    };
  }, []);

  useEffect(() => {
    markupPathologyRef.current.markup = {};
    setMarkupPathology({});
    if (pathology) {
      setDefaultDataState(pathology);
    }
  }, [pathology]);

  useEffect(() => {
    if (
      !markup &&
      imageName &&
      markupPathologyRef.current?.markup[imageName]?.length &&
      markupPathologyRef.current?.markup[imageName] !==
        markupPathology[imageName]
    ) {
      postMarkup();
    }
  }, [markup, markupPathology, imageName]);

  useEffect(() => {
    if (error) {
      alert("Нет иследований для разметки");
      document.location = "/";
    }
  }, [error]);

  useEffect(() => {
    if (!pathology) return;
    const imageName = pathology.markup[activeIndex].img;
    setMarkupSteps([]);
    setMarkupPathology((markup) => {
      return {
        ...markup,
        [imageName]:
          markup[imageName] || pathology.markup[activeIndex].markupData || [],
      };
    });
  }, [activeIndex, pathology]);

  useEffect(() => {
    if (!pathology) return;
    markupPathologyRef.current = {
      pathology: pathology,
      markup: markupPathology,
    };
  }, [markupPathology, pathology]);

  useEffect(() => {
    if (!pathology) return;

    const img = pathologyImages[pathology.id]
      ? pathologyImages[pathology.id][pathology.markup[activeIndex].img]
      : undefined;

    if (img) {
      setImage(img);
      setImageName(pathology.markup[activeIndex].img);
      return;
    }

    axios
      .get(
        `/api/v0.1/image/${pathology.id}/${pathology.markup[activeIndex].img}`,
        {
          responseType: "blob",
        }
      )
      .then((response) => {
        const imageUrl = window.URL.createObjectURL(response.data);

        setPathologyImages({
          ...pathologyImages,
          [pathology.id]: {
            ...pathologyImages[pathology.id],
            [pathology.markup[activeIndex].img]: imageUrl,
          },
        });

        setImage(imageUrl);
        setImageName(pathology.markup[activeIndex].img);
      })
      .catch(() => {
        setActiveIndex(activeIndex - 1);
      });

    return () => {
      setImage("");
      setImageName("");
      setImageData({} as ImageData);
    };
  }, [pathology, activeIndex, pathologyImages]);

  useEffect(() => {
    setRegionState(region);
  }, [region]);

  const handleChange = (e: {
    target: {
      name: string;
      value: string;
    };
  }) => {
    const { name, value } = e.target;
    setDefaultDataState(
      (prevState) =>
        prevState && {
          ...prevState,
          [name]: value,
        }
    );
  };

  const [, setRegionState] = useRecoilState(regionState);

  const postMarkup = useCallback(() => {
    const payload = markupPathologyRef.current?.pathology?.markup.reduce(
      (res, item) => {
        if (markupPathologyRef.current?.markup[item.img]?.length) {
          return [
            ...res,
            {
              img: item.img,
              markupData: markupPathologyRef.current?.markup[item.img]
                .filter((item) => {
                  return !(
                    item.markupType === "segment" && item.points.length === 1
                  );
                })
                .map((item) => ({
                  name: item.name,
                  points: item.points,
                  side: item.side,
                  degree: item.degree,
                  zone: item.zone,
                  spineSide: item.spineSide,
                })),
            },
          ];
        } else {
          return res;
        }
      },
      [] as any
    );

    return axios
      .post(
        `/api/v0.1/upload/study-markup/${markupPathologyRef.current?.pathology.id}`,
        payload
      )
      .catch(() => {
        console.log("Ошибка записи разметки иследования");
      });
  }, []);

  const sendReturnMarkup = useCallback(() => {
    if (!pathology) return;
    axios.get(`/api/v0.1/validation/reject/${pathology.id}`).catch(() => {
      alert("Ошибка возврата на доработку иследования");
    });
    markupPathologyRef.current.markup = {};
    setMarkupPathology({});
    getPathology(region);
  }, [region, pathology]);

  const prevImage = useCallback(() => {
    if (!pathology) return;

    setActiveIndex((activeIndex) =>
      activeIndex === 0 ? pathology.markup.length - 1 : activeIndex - 1
    );
  }, [pathology]);

  const nextImage = useCallback(() => {
    if (!pathology) return;

    setActiveIndex((activeIndex) =>
      activeIndex < pathology.markup.length - 1 ? activeIndex + 1 : 0
    );
  }, [pathology]);

  const validate = () => {
    const error = { value: false, text: "" };
    if (!error.value) {
      if (
        regionConfig[region].requiredFields.includes("visiblePart") &&
        !defaultDataState?.visiblePart
      ) {
        error.value = true;
        error.text = `Не заполнено поле "Что видно на снимки"`;
      }
      if (
        regionConfig[region].requiredFields.includes("mode") &&
        !defaultDataState?.mode
      ) {
        error.value = true;
        error.text = "Не заполнено поле Режим";
      }
      if (
        regionConfig[region].requiredFields.includes("projection") &&
        !defaultDataState?.projection
      ) {
        error.value = true;
        error.text = "Не заполнено поле Проекция";
      }
    }

    if (defaultDataState && pathology) {
      if (!error.value) {
        postMarkup()
          .then(() => {
            const requestData = {
              sequence: defaultDataState.sequence,
              visiblePart: defaultDataState.visiblePart,
              mode: defaultDataState.mode || undefined,
              projection: defaultDataState.projection,
            };

            markupPathologyRef.current.markup = {};
            setMarkupPathology({});
            isMarkup ? getMarkupPathology(region) : getPathology(region);

            isMarkup
              ? saveMarkUpPathology(requestData, pathology.id)
              : validatePathology(requestData, pathology.id);
          })
          .catch(() => {
            alert("Внутреняя ошибка сервера, попробуйте позже");
          });
      } else NotificationSend("error", error.text);
    }
  };

  const returnMainPage = useCallback(() => {
    setImage("");
    setImageName("");
    setImageData({} as ImageData);
    history.go(-1);
  }, []);

  useEffect(() => {
    const handlerArrow = (e: any) => {
      if (e.key === "ArrowRight") {
        nextImage();
      } else if (e.key === "ArrowLeft") {
        prevImage();
      }
    };

    window.addEventListener("keydown", handlerArrow);

    return () => {
      window.removeEventListener("keydown", handlerArrow);
    };
  }, [pathology]);

  if (!pathology || markupPathology[imageName] === undefined || loading) {
    console.log("pathology", pathology);
    console.log("markupPathology", markupPathology);
    console.log("imageName", imageName);
    return <Loader message="Загрузка иследования..." />;
  }
  return (
    <>
      {/*<div className="row">*/}
      {/*  <div className="col-6">*/}
      {/*    <div className="row">*/}
      {/*      <SideSelect*/}
      {/*        side="left"*/}
      {/*        setDefaultDataState={setDefaultDataState}*/}
      {/*        defaultDataState={defaultDataState}*/}
      {/*      />*/}
      {/*    </div>*/}
      {/*  </div>*/}
      {/*  <div className="col-6">*/}
      {/*    <div className="row">*/}
      {/*      <SideSelect*/}
      {/*        side="right"*/}
      {/*        setDefaultDataState={setDefaultDataState}*/}
      {/*        defaultDataState={defaultDataState}*/}
      {/*      />*/}
      {/*    </div>*/}
      {/*  </div>*/}
      {/*</div>*/}
      <div className="row pt-3">
        <div id="imageViewer">
          <div className="row">
            <div className="col-12">
              {/*<div className="row">*/}
              {/*  <div className="col-6">*/}
              {/*    <h5>*/}
              {/*      Иследование <strong>{pathology.sequence}</strong>*/}
              {/*    </h5>*/}
              {/*  </div>*/}
              {/*  <div className="col-6">*/}
              {/*    <span className="close" onClick={returnMainPage}>*/}
              {/*      &times;*/}
              {/*    </span>*/}
              {/*  </div>*/}
              {/*</div>*/}
              {/*<br />*/}
              <div className="row">
                <div className="col">
                  <div className="form-group">
                    <label>Название иследования</label>
                    <input
                      type="text"
                      readOnly
                      className="form-control"
                      value={pathology.sequence}
                    />
                  </div>
                </div>
                <div className="col">
                  <div className="form-group">
                    <label htmlFor="pathologyLeftSelect">
                      Что видно на снимке
                    </label>
                    <select
                      defaultValue={pathology.visiblePart}
                      className="form-control"
                      name="visiblePart"
                      onChange={handleChange}
                    >
                      {visibleParts[region].map((part) => (
                        <option key={part.value} value={part.value}>
                          {part.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {modes[region] && (
                  <div className="col">
                    <div className="form-group">
                      <label htmlFor="pathologyLeftSelect">Режим</label>
                      <select
                        defaultValue={pathology.mode}
                        onChange={handleChange}
                        name="mode"
                        className="form-control"
                      >
                        {modes[region].map((mode) => (
                          <option key={mode.value} value={mode.value}>
                            {mode.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
                <div className="col">
                  <div className="form-group">
                    <label htmlFor="pathologyLeftSelect">Проекция</label>
                    <select
                      defaultValue={pathology.projection}
                      onChange={handleChange}
                      name="projection"
                      className="form-control"
                    >
                      {projections[region].map((projection) => (
                        <option key={projection.value} value={projection.value}>
                          {projection.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {!isMarkup && (
                  <div className="col">
                    <div className="form-group">
                      <label htmlFor="intern">Пользователь</label>
                      <input
                        type="text"
                        name="intern"
                        className="form-control"
                        readOnly
                        value={pathology.intern}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <Markup
            imageSrc={image}
            pixelSpacing={pathology.metadata.pixelSpacing || 1}
            prevImage={prevImage}
            nextImage={nextImage}
          />
          <div className="control">
            <div className="row">
              <div className="col-3">
                <button
                  onClick={() => validate()}
                  className="btn btn-success w-100"
                >
                  {isMarkup ? "Сохранить разметку" : "Валидировать иследование"}
                </button>
              </div>
              <div className="col-3">
                <button
                  onClick={() => {
                    deletePathology(pathology.id);
                    markupPathologyRef.current.markup = {};
                    setMarkupPathology({});
                    isMarkup
                      ? getMarkupPathology(region)
                      : getPathology(region);
                  }}
                  className="btn btn-danger w-100"
                >
                  Удалить иследование
                </button>
              </div>
              <div className="col-3">
                {isMarkup ? (
                  <button
                    onClick={() => {
                      if (
                        confirm(
                          "Разметка не сохраниться, вы уверенны что хотите перейти без сохранения?"
                        )
                      ) {
                        markupPathologyRef.current.markup = {};
                        setMarkupPathology({});
                        getMarkupPathology(region);
                      }
                    }}
                    className="btn btn-secondary w-100"
                  >
                    Следующие иследование
                  </button>
                ) : (
                  <button
                    onClick={sendReturnMarkup}
                    className="btn btn-secondary w-100"
                  >
                    Вернуть на доработку
                  </button>
                )}
              </div>
              <div className="col-3">
                <button
                  onClick={returnMainPage}
                  className="btn btn-primary w-100"
                >
                  {isMarkup ? "Выйти из разметки" : "Выйти из валидации"}
                </button>
              </div>
            </div>
          </div>
          {/*{returnMarkup && (*/}
          {/*  <ReturnMarkup*/}
          {/*    onClose={() => setReturnMarkup(false)}*/}
          {/*    onSend={sendReturnMarkup}*/}
          {/*  />*/}
          {/*)}*/}
        </div>
      </div>
      <hr />
    </>
  );
};
